import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface InventoryRow {
  productId: string;
  productName: string;
  category: string;
  quantityOnHand: number;
  lowThreshold: number;
  status: 'OK' | 'LOW' | 'OUT_OF_STOCK';
  price: string;
}

/**
 * Negative quantities are legitimate here: they mean more was sold than
 * production recorded, and are a prompt to count — not an error to hide.
 */
function stockLabel(row: InventoryRow) {
  if (row.quantityOnHand < 0) {
    return { text: 'Oversold — count', cls: 'bg-red-100 text-red-700 border border-dashed border-red-300' };
  }
  if (row.quantityOnHand === 0) return { text: 'Out of stock', cls: 'bg-red-100 text-red-700' };
  if (row.status === 'LOW') return { text: 'Low', cls: 'bg-amber-100 text-amber-800' };
  return { text: 'OK', cls: 'bg-green-100 text-green-800' };
}

export default function InventoryPanel({ branchId, branchName }: { branchId: string; branchName?: string }) {
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'attention'>('all');
  const [adjusting, setAdjusting] = useState<InventoryRow | null>(null);

  const load = async () => {
    if (!branchId) return;
    try {
      // /api/inventory carries lowThreshold; the reports endpoint does not
      const res = await apiClient.getInventory(branchId);
      setRows(Array.isArray(res.data) ? res.data : res.data.inventory || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const needsAttention = rows.filter((r) => r.quantityOnHand <= 0 || r.status === 'LOW');
  const shown = filter === 'attention' ? needsAttention : rows;
  const oversold = rows.filter((r) => r.quantityOnHand < 0).length;
  const outOfStock = rows.filter((r) => r.quantityOnHand === 0).length;
  const healthy = rows.filter((r) => r.quantityOnHand > 0 && r.status === 'OK').length;
  const stockValue = rows.reduce(
    (sum, r) => sum + Math.max(0, r.quantityOnHand) * (Number(r.price) || 0),
    0,
  );

  if (loading) return <div className="text-gray-500 py-8">Loading inventory…</div>;
  if (error) return <div className="bg-red-50 text-red-700 rounded-lg p-4">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Healthy</h3>
          <p className="mt-1 text-3xl font-bold text-green-700 tabular-nums">{healthy}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Out of stock</h3>
          <p className="mt-1 text-3xl font-bold text-red-600 tabular-nums">{outOfStock}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Oversold</h3>
          <p className="mt-1 text-3xl font-bold text-red-600 tabular-nums">{oversold}</p>
          <p className="text-xs text-gray-500 mt-1">needs a stock count</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Stock value</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">R {stockValue.toFixed(0)}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Stock on hand</h3>
            {branchName && <p className="text-sm text-gray-500">{branchName}</p>}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded text-sm font-medium ${
                filter === 'all' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All {rows.length}
            </button>
            <button
              onClick={() => setFilter('attention')}
              className={`px-3 py-1.5 rounded text-sm font-medium ${
                filter === 'attention' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Needs attention {needsAttention.length}
            </button>
            <button onClick={load} className="px-3 py-1.5 rounded text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left py-3 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Product</th>
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Category</th>
                <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">On hand</th>
                <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Reorder at</th>
                <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Price</th>
                <th className="text-right py-3 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const label = stockLabel(row);
                return (
                  <tr key={row.productId} className="border-b last:border-0 hover:bg-amber-50/40">
                    <td className="py-3 px-6 font-medium text-gray-900">{row.productName}</td>
                    <td className="py-3 px-4 text-gray-500 text-sm capitalize">{row.category}</td>
                    <td
                      className={`py-3 px-4 text-right tabular-nums font-semibold ${
                        row.quantityOnHand < 0 ? 'text-red-600' : 'text-gray-900'
                      }`}
                    >
                      {row.quantityOnHand}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-gray-500">{row.lowThreshold}</td>
                    <td className="py-3 px-4 text-right tabular-nums text-gray-600">R {Number(row.price).toFixed(2)}</td>
                    <td className="py-3 px-6 text-right">
                      <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${label.cls}`}>
                        {label.text}
                      </span>
                      <button
                        onClick={() => setAdjusting(row)}
                        className="ml-3 text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                      >
                        Adjust
                      </button>
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-gray-500">
                    Nothing needs attention right now.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {adjusting && (
        <AdjustModal
          row={adjusting}
          branchId={branchId}
          onClose={() => setAdjusting(null)}
          onDone={() => {
            setAdjusting(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function AdjustModal({
  row,
  branchId,
  onClose,
  onDone,
}: {
  row: InventoryRow;
  branchId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [counted, setCounted] = useState(String(row.quantityOnHand));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const delta = (Number(counted) || 0) - row.quantityOnHand;

  const submit = async () => {
    if (!reason.trim()) {
      setErr('A reason is required — it goes into the audit log.');
      return;
    }
    setSaving(true);
    try {
      await apiClient.adjustInventory(row.productId, branchId, Number(counted), 'COUNT', reason.trim());
      onDone();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Adjustment failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-gray-900">Stock count — {row.productName}</h3>
        <p className="text-sm text-gray-500 mt-1">
          System says <span className="tabular-nums font-semibold">{row.quantityOnHand}</span>. Enter what you actually counted.
        </p>

        <label className="block text-sm font-medium text-gray-700 mt-5 mb-1">Counted quantity</label>
        <input
          type="number"
          value={counted}
          onChange={(e) => setCounted(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg tabular-nums text-lg font-semibold focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        />

        {delta !== 0 && (
          <p className={`text-sm mt-2 font-medium ${delta < 0 ? 'text-red-600' : 'text-green-700'}`}>
            {delta > 0 ? '+' : ''}
            {delta} against the system figure
          </p>
        )}

        <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Reason</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. recount after morning production"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        />

        {err && <p className="text-sm text-red-600 mt-2">{err}</p>}

        <div className="flex gap-3 mt-5">
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg"
          >
            {saving ? 'Saving…' : 'Save count'}
          </button>
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 font-medium text-gray-700">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
